/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikHelpers, useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, FormControl, FormHelperText, OutlinedInput, Typography } from '@mui/material';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserUserApiService } from '../../services/SuperuserUserApiService';
import { useFormStyles } from '../../styles/form';
import { ApiClientError } from '../../types/api/ApiClientError';
import { useApiValidation } from '../../utils/useApiValidation';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { useValidationTranslation } from '../../utils/useValidationTranslation';
import { emailTestValidationSchema, initialValues } from './EmailTestPage.const';
import { useEmailTestPageStyles } from './EmailTestPage.style';
import { EmailTestFormValues } from './EmailTestPage.types';

type ModalState = {
  successModal: {
    isOpen: boolean;
  };
  errorModal: {
    isOpen: boolean;
    errorText?: string;
  };
};

export function EmailTestPage() {
  const { hasFieldApiError, getFieldApiError, resetFieldApiError, resetApiError, setApiError } = useApiValidation();
  const { vtr } = useValidationTranslation();
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useEmailTestPageStyles();
  const formClasses = useFormStyles();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [modalState, setModalState] = useObjectState<ModalState>({
    successModal: {
      isOpen: false,
    },
    errorModal: {
      isOpen: false,
      errorText: '',
    },
  });

  useDocumentTitle(t(`${Namespaces.Common}:page_titles.email_test`));

  const onSubmit = async (values: EmailTestFormValues, actions: FormikHelpers<EmailTestFormValues>) => {
    resetApiError();
    setIsFetching(true);
    try {
      await SuperuserUserApiService.createEmailTest({ data: values });
      actions.resetForm();
      setModalState({
        successModal: {
          isOpen: true,
        },
      });
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      const responseData = error.response?.data;
      if (!responseData) {
        setModalState({
          errorModal: {
            isOpen: true,
          },
        });
      } else {
        setApiError(error);
        const jsonObject = JSON.stringify(responseData, null, 2);
        setModalState({
          errorModal: {
            isOpen: true,
            errorText: jsonObject,
          },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const formik = useFormik<EmailTestFormValues>({
    initialValues,
    validationSchema: emailTestValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: onSubmit,
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  const hasError = (name: keyof EmailTestFormValues): boolean => {
    return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
  };

  const getError = (name: keyof EmailTestFormValues): string => {
    const error = errors[name] || getFieldApiError(name);
    return error || '';
  };

  return (
    <Box>
      <Typography variant="h2" className={classes.title}>
        {t('email_test.title')}
      </Typography>
      <form onSubmit={handleSubmit}>
        <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('email')}>
          <OutlinedInput
            name="email"
            placeholder={t('email_test.email')}
            value={values.email}
            onChange={event => {
              resetFieldApiError('email');
              handleChange(event);
            }}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('email') && (
            <FormHelperText>
              {vtr(getError('email'), {
                field: t('email_test.email'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('subject')}>
          <OutlinedInput
            name="subject"
            placeholder={t('email_test.subject')}
            value={values.subject}
            onChange={event => {
              resetFieldApiError('subject');
              handleChange(event);
            }}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('subject') && (
            <FormHelperText>
              {vtr(getError('subject'), {
                field: t('email_test.subject'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('html')}>
          <OutlinedInput
            name="html"
            placeholder={t('email_test.html')}
            value={values.html}
            onChange={event => {
              resetFieldApiError('html');
              handleChange(event);
            }}
            onBlur={handleBlur}
            disabled={isFetching}
            multiline={true}
            rows={3}
            maxRows={7}
          />
          {hasError('html') && (
            <FormHelperText>
              {vtr(getError('html'), {
                field: t('email_test.html'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <Button type="submit" variant="contained" className={classes.submitBtn} disabled={isFetching}>
          {t('buttons.submit')}
        </Button>
      </form>
      <SimpleNotifyModal
        open={modalState.successModal.isOpen}
        message={t('email_test.success-modal')}
        onOk={() =>
          setModalState({
            successModal: {
              isOpen: false,
            },
          })
        }
      />
      <SimpleNotifyModal
        open={modalState.errorModal.isOpen}
        message={
          modalState.errorModal.errorText ? (
            <pre className={classes.modalError}>{modalState.errorModal.errorText}</pre>
          ) : (
            t(`${Namespaces.Common}:errors.unexpected_error`)
          )
        }
        onOk={() =>
          setModalState({
            errorModal: { isOpen: false, errorText: '' },
          })
        }
      />
    </Box>
  );
}
